import React, { useState, useRef, useCallback, useEffect } from "react";
import ScreenRecordingPreview, {
  ScreenRecordingPreview as PreviewType,
  RecordingSubmission,
} from "./ScreenRecordingPreview";
import SharedScreenStreamManager from "../SharedScreenStreamManager";
import ScreenRecordingToolBox from "../../RecordingToolBox";
import PenHighlightOverlay from "../PenHighlightOverlay";
import { generateVideoThumbnail } from "../../../../utils/feedbackUtils";

interface ScreenRecordingManagerProps {
  onToolClick?: (toolId: string) => void;
  onClose?: () => void;
  onRecordingSubmit?: (submission: RecordingSubmission) => void;
  uniqueFeedbackId?: string;
  compact?: boolean;
  isFullscreen?: boolean;
}

// ✅ UPDATED: Smooth 60 FPS video optimization configuration
const VIDEO_OPTIMIZATION_CONFIG = {
  MAX_VIDEO_BITRATE: 8000000,  // 8 Mbps (increased from 5 Mbps)
  MIN_VIDEO_BITRATE: 5000000,  // 5 Mbps (increased from 3 Mbps)
  AUDIO_BITRATE_NORMAL: 128000, // 128 kbps (increased from 64 kbps)
  AUDIO_BITRATE_SMALL: 96000,   // 96 kbps (increased from 48 kbps)
  MAX_CAPTURE_WIDTH: 1920,      // Full HD (increased from 1440)
  MAX_CAPTURE_HEIGHT: 1080,     // Full HD (increased from 720)
  DEFAULT_FRAMERATE: 60,        // ✅ 60 FPS for smooth recording (was 24)
  SMALL_VIDEO_FRAMERATE: 30,    // ✅ 30 FPS minimum (was 15)
  SMALL_VIDEO_THRESHOLD: 800 * 600,
  DATA_COLLECTION_INTERVAL: 100,
};

// ✅ NEW: Maximum recording duration in seconds
const MAX_RECORDING_SECONDS = 30;

const ScreenRecordingManager: React.FC<ScreenRecordingManagerProps> = ({
  onToolClick,
  onClose,
  onRecordingSubmit,
  uniqueFeedbackId,
  compact = false,
  isFullscreen = false,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  // ✅ UPDATED: Start at MAX_RECORDING_SECONDS for countdown
  const [recordingTime, setRecordingTime] = useState(MAX_RECORDING_SECONDS);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [recordingPreview, setRecordingPreview] = useState<PreviewType | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [drawingMode, setDrawingMode] = useState(false); // ✅ Drawing mode state

  const [annotationDataUrl, setAnnotationDataUrl] = useState<string>("");
  const annotationCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const intervalRef = useRef<number | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingStartTime = useRef<number>(0);
  // ✅ NEW: Track elapsed seconds for actual duration calculation
  const elapsedSecondsRef = useRef<number>(0);
  const iframeDimensionsRef = useRef<any | null>(null);
  const currentFeedbackIdRef = useRef<string | undefined>(uniqueFeedbackId);
  const sharedStreamManager = useRef(SharedScreenStreamManager.getInstance());
  const consumerIdRef = useRef(`recording-${Date.now()}`);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  
  const microphoneStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    console.log("🎥 ScreenRecordingManager - uniqueFeedbackId changed:", {
      previous: currentFeedbackIdRef.current,
      new: uniqueFeedbackId,
      type: typeof uniqueFeedbackId,
      isValid: uniqueFeedbackId && String(uniqueFeedbackId).trim() !== "",
    });
    currentFeedbackIdRef.current = uniqueFeedbackId;
  }, [uniqueFeedbackId]);

  const createRecordingPreview = useCallback(
    (blob: Blob, duration: number): PreviewType => {
      const dataUrl = URL.createObjectURL(blob);
      return {
        dataUrl,
        blob,
        duration,
        timestamp: Date.now(),
      };
    },
    []
  );

  const captureAnnotationCanvas = useCallback((): string => {
    const canvas = annotationCanvasRef.current;
    if (!canvas) return "";

    try {
      // Only return the canvas if it has actual drawings (non-transparent pixels)
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hasContent = imageData.data.some(
          (value, index) => index % 4 === 3 && value > 0,
        );
        if (!hasContent) {
          console.log("ℹ️ Annotation canvas is blank — skipping");
          return "";
        }
      }
      const dataUrl = canvas.toDataURL("image/png");
      console.log("✅ Annotation canvas captured:", dataUrl.substring(0, 50));
      return dataUrl;
    } catch (error) {
      console.error("❌ Failed to capture annotation canvas:", error);
      return "";
    }
  }, []);


  // ✅ UPDATED: Get optimized recording options with higher bitrates for smooth playback
  const getOptimizedRecordingOptions = useCallback((dimensions: any) => {
    const pixels = dimensions.width * dimensions.height;
    let videoBitrate;

    if (pixels > 1920 * 1080) {
      videoBitrate = 8000000;  // 8 Mbps for 4K+
    } else if (pixels > 1280 * 720) {
      videoBitrate = 6000000;  // 6 Mbps for 1080p
    } else {
      videoBitrate = 4000000;  // 4 Mbps for 720p
    }

    const audioBitrate = VIDEO_OPTIMIZATION_CONFIG.AUDIO_BITRATE_NORMAL;

    console.log("🎬 Recording options (smooth mode):", {
      videoBitrate: `${(videoBitrate / 1000000).toFixed(1)} Mbps`,
      audioBitrate: `${(audioBitrate / 1000).toFixed(0)} kbps`,
      resolution: `${dimensions.width}x${dimensions.height}`,
      frameRate: "60 FPS",
    });

    return {
      videoBitsPerSecond: videoBitrate,
      audioBitsPerSecond: audioBitrate,
    };
  }, []);

  const getOptimizedStreamConstraints = useCallback((dimensions: any) => {
    let captureWidth = dimensions.width;
    let captureHeight = dimensions.height;
    let frameRate = VIDEO_OPTIMIZATION_CONFIG.DEFAULT_FRAMERATE;

    if (
      captureWidth > VIDEO_OPTIMIZATION_CONFIG.MAX_CAPTURE_WIDTH ||
      captureHeight > VIDEO_OPTIMIZATION_CONFIG.MAX_CAPTURE_HEIGHT
    ) {
      const aspectRatio = captureWidth / captureHeight;
      if (aspectRatio > 1) {
        captureWidth = VIDEO_OPTIMIZATION_CONFIG.MAX_CAPTURE_WIDTH;
        captureHeight = Math.round(VIDEO_OPTIMIZATION_CONFIG.MAX_CAPTURE_WIDTH / aspectRatio);
      } else {
        captureHeight = VIDEO_OPTIMIZATION_CONFIG.MAX_CAPTURE_HEIGHT;
        captureWidth = Math.round(VIDEO_OPTIMIZATION_CONFIG.MAX_CAPTURE_HEIGHT * aspectRatio);
      }
    }

    console.log("🎬 Stream constraints (smooth 60 FPS mode):", {
      captureSize: `${captureWidth}x${captureHeight}`,
      frameRate: `${frameRate} FPS`,
      quality: "High (Smooth)",
    });

    return {
      preferredWidth: captureWidth,
      preferredHeight: captureHeight,
      frameRate,
      includeAudio: true,
    };
  }, []);

  const createOptimizedBlob = useCallback((chunks: Blob[], mimeType: string): Blob => {
    let blob = new Blob(chunks, { type: mimeType });
    if (!blob.type.includes("mp4")) {
      blob = new Blob(chunks, { type: "video/mp4" });
    }
    return blob;
  }, []);

  const getMicrophoneStream = useCallback(async (): Promise<MediaStream> => {
    try {
      console.log("🎤 Requesting microphone access...");
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      console.log("✅ Microphone access granted");
      return micStream;
    } catch (error) {
      console.error("❌ Failed to get microphone access:", error);
      throw new Error("Microphone access denied or unavailable");
    }
  }, []);

  const startOptimizedRecording = useCallback(
    async (iframeDimensions: any, shouldShowPreview: boolean = true) => {
      try {
        const streamConstraints = getOptimizedStreamConstraints(iframeDimensions);
        const screenStream = await sharedStreamManager.current.getSharedStream(streamConstraints);

        if (!screenStream) throw new Error("Failed to get optimized stream");

        let micStream: MediaStream | null = null;
        try {
          micStream = await getMicrophoneStream();
          microphoneStreamRef.current = micStream;
        } catch (error) {
          console.warn("⚠️ Could not access microphone:", error);
          alert("Microphone access was denied. Recording will continue without audio.");
        }

        const combinedStream = new MediaStream();
        
        screenStream.getVideoTracks().forEach(track => {
          combinedStream.addTrack(track);
          console.log("✅ Added screen video track");
        });
        
        if (micStream) {
          micStream.getAudioTracks().forEach(track => {
            combinedStream.addTrack(track);
            console.log("✅ Added microphone audio track");
          });
        } else {
          screenStream.getAudioTracks().forEach(track => {
            combinedStream.addTrack(track);
            console.log("✅ Added screen audio track (fallback)");
          });
        }

        console.log("🎬 Combined stream tracks:", {
          video: combinedStream.getVideoTracks().length,
          audio: combinedStream.getAudioTracks().length,
        });

        const recordingOptions = getOptimizedRecordingOptions(iframeDimensions);

        let mimeType = "";
        if (MediaRecorder.isTypeSupported("video/mp4;codecs=h264,aac")) {
          mimeType = "video/mp4;codecs=h264,aac";
        } else if (MediaRecorder.isTypeSupported("video/mp4")) {
          mimeType = "video/mp4";
        } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")) {
          mimeType = "video/webm;codecs=vp9,opus";
        } else {
          mimeType = "video/webm";
        }

        const mediaRecorder = new MediaRecorder(combinedStream, {
          mimeType,
          ...recordingOptions,
        });

        const chunks: Blob[] = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            chunks.push(event.data);
          }
        };

        if (shouldShowPreview) {
          mediaRecorder.onstop = () => {
            const optimizedBlob = createOptimizedBlob(chunks, mimeType);
            setRecordedBlob(optimizedBlob);
            
            // ✅ UPDATED: Use elapsed seconds for actual duration
            const actualDuration = elapsedSecondsRef.current;
            const fileSizeInMB = (optimizedBlob.size / (1024 * 1024)).toFixed(2);
            
            console.log("📊 ========== SMOOTH SCREEN RECORDING CREATED ==========");
            console.log(`📦 File Size: ${fileSizeInMB} MB`);
            console.log(`🎬 MIME Type: ${optimizedBlob.type}`);
            console.log(`⏱️ Duration: ${actualDuration} seconds`);
            console.log(`🎯 Frame Rate: 60 FPS (Smooth)`);
            console.log("📊 ====================================================");
            
            const annotationImage = captureAnnotationCanvas();
            setAnnotationDataUrl(annotationImage);
            console.log("✅ Annotations captured for preview");
            
            const preview = createRecordingPreview(optimizedBlob, actualDuration);
            setRecordingPreview(preview);
            setShowPreview(true);

            if (microphoneStreamRef.current) {
              microphoneStreamRef.current.getTracks().forEach(track => {
                track.stop();
                console.log("🛑 Stopped microphone track");
              });
              microphoneStreamRef.current = null;
            }
          };
        } else {
          console.log("🔇 Preview disabled for this recording session (restart mode)");
        }

        mediaRecorder.start(100);
        return mediaRecorder;
      } catch (error) {
        console.error("❌ Failed to start optimized recording:", error);
        if (microphoneStreamRef.current) {
          microphoneStreamRef.current.getTracks().forEach(track => track.stop());
          microphoneStreamRef.current = null;
        }
        throw error;
      }
    },
    [
      getOptimizedStreamConstraints,
      getOptimizedRecordingOptions,
      createOptimizedBlob,
      createRecordingPreview,
      captureAnnotationCanvas,
      getMicrophoneStream,
    ]
  );

  const detectNavigationBarHeight = useCallback((iframe: HTMLIFrameElement): number => {
    const screenWidth = window.innerWidth;
    if (screenWidth >= 2560) return 90;
    else if (screenWidth >= 1920) return 80;
    else if (screenWidth >= 1440) return 65;
    else if (screenWidth >= 1280) return 60;
    return 55;
  }, []);

  const getIframeDimensions = useCallback(() => {
    const iframe = document.querySelector("iframe") as HTMLIFrameElement;
    if (!iframe) return null;

    const rect = iframe.getBoundingClientRect();
    const outerWidth = window.outerWidth;
    const outerHeight = window.outerHeight;
    const innerWidth = window.innerWidth;
    const innerHeight = window.innerHeight;
    const chromeTop = outerHeight - innerHeight;
    const chromeLeft = Math.max(0, (outerWidth - innerWidth) / 2);
    const screenX = window.screenX || (window as any).screenLeft || 0;
    const screenY = window.screenY || (window as any).screenTop || 0;

    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      iframe: iframe,
      viewportLeft: rect.left,
      viewportTop: rect.top,
      navigationBarHeight: 0,
      viewportWidth: innerWidth,
      viewportHeight: innerHeight,
      outerWidth,
      outerHeight,
      chromeTop,
      chromeLeft,
      screenX,
      screenY,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
    };
  }, []);

  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // ✅ UPDATED: Timer counts DOWN from MAX_RECORDING_SECONDS
  const startTimer = useCallback(() => {
    setRecordingTime(MAX_RECORDING_SECONDS);
    elapsedSecondsRef.current = 0;
    recordingStartTime.current = Date.now();
    intervalRef.current = window.setInterval(() => {
      setRecordingTime((prev) => {
        elapsedSecondsRef.current += 1;
        if (prev <= 1) {
          // Reached zero — clear interval, auto-stop will be triggered by useEffect
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // ✅ UPDATED: Reset back to MAX_RECORDING_SECONDS
  const resetTimer = useCallback(() => {
    setRecordingTime(MAX_RECORDING_SECONDS);
    elapsedSecondsRef.current = 0;
    recordingStartTime.current = 0;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const handleStartRecording = useCallback(async () => {
    if (isRecording) return;

    try {
      setIsLoading(true);
      setShowPreview(false);
      setRecordingPreview(null);
      setAnnotationDataUrl("");

      const iframeDimensions = getIframeDimensions();
      if (!iframeDimensions) {
        throw new Error("No iframe found to record. Please load a website first.");
      }

      setRecordedBlob(null);
      chunksRef.current = [];
      iframeDimensionsRef.current = iframeDimensions;

      console.log("🎬 Requesting screen share and microphone permission...");
      const mediaRecorder = await startOptimizedRecording(iframeDimensions);
      
      console.log("⏳ Waiting 1 second for UI to settle after permission popup...");
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.onerror = (event) => {
        console.error("❌ MediaRecorder error:", event);
        setIsRecording(false);
        stopTimer();
        if (microphoneStreamRef.current) {
          microphoneStreamRef.current.getTracks().forEach(track => track.stop());
          microphoneStreamRef.current = null;
        }
      };

      setIsRecording(true);
      startTimer();
      window.dispatchEvent(new CustomEvent('feebdak-recording-state', { detail: { isRecording: true } }));
      console.log(`✅ Recording started — ${MAX_RECORDING_SECONDS}s countdown begun`);
    } catch (error) {
      console.error("❌ Failed to start recording:", error);
      setIsRecording(false);
      if (microphoneStreamRef.current) {
        microphoneStreamRef.current.getTracks().forEach(track => track.stop());
        microphoneStreamRef.current = null;
      }
      alert(`Failed to start recording: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsLoading(false);
    }
  }, [isRecording, startTimer, stopTimer, getIframeDimensions, startOptimizedRecording]);

  const handleStopRecording = useCallback(() => {
    if (!isRecording) return;

    try {
      stopTimer();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);
      window.dispatchEvent(new CustomEvent('feebdak-recording-state', { detail: { isRecording: false } }));
    } catch (error) {
      console.error("❌ Failed to stop recording:", error);
      setIsRecording(false);
      window.dispatchEvent(new CustomEvent('feebdak-recording-state', { detail: { isRecording: false } }));
      stopTimer();
      if (microphoneStreamRef.current) {
        microphoneStreamRef.current.getTracks().forEach(track => track.stop());
        microphoneStreamRef.current = null;
      }
    }
  }, [isRecording, stopTimer]);

  // ✅ NEW: Auto-stop when countdown reaches 0
  useEffect(() => {
    if (recordingTime === 0 && isRecording) {
      console.log("⏱️ Countdown reached 0 — auto-stopping recording");
      handleStopRecording();
    }
  }, [recordingTime, isRecording, handleStopRecording]);

  // ✅ Handle restart recording
  const handleRestartRecording = useCallback(async () => {
    console.log("🔄 Restarting recording...");
    
    if (isRecording) {
      try {
        stopTimer();
        
        if (mediaRecorderRef.current) {
          mediaRecorderRef.current.onstop = null;
          
          if (mediaRecorderRef.current.state === "recording") {
            mediaRecorderRef.current.stop();
          }
        }
        
        if (microphoneStreamRef.current) {
          microphoneStreamRef.current.getTracks().forEach(track => track.stop());
          microphoneStreamRef.current = null;
        }
        
        setIsRecording(false);
        console.log("✅ Current recording stopped without preview");
      } catch (error) {
        console.error("❌ Failed to stop current recording:", error);
      }
    }

    setRecordedBlob(null);
    setRecordingPreview(null);
    setShowPreview(false);
    setAnnotationDataUrl("");
    chunksRef.current = [];
    resetTimer();
    mediaRecorderRef.current = null;

    await new Promise(resolve => setTimeout(resolve, 300));

    console.log("▶️ Starting fresh recording with new 30s countdown...");
    await handleStartRecording();
  }, [isRecording, stopTimer, resetTimer, handleStartRecording]);

  const handleDeleteRecording = useCallback(() => {
    if (isRecording) {
      handleStopRecording();
    }
    setRecordedBlob(null);
    setRecordingPreview(null);
    setShowPreview(false);
    setIsProcessing(false);
    setAnnotationDataUrl("");
    chunksRef.current = [];
    resetTimer();
    mediaRecorderRef.current = null;
    iframeDimensionsRef.current = null;
  }, [isRecording, handleStopRecording, resetTimer]);

  const handleDownloadRecording = useCallback(() => {
    if (!recordedBlob) {
      alert("No recording available to download");
      return;
    }

    try {
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, "-");
      const duration = formatTime(elapsedSecondsRef.current);
      const dimensions = iframeDimensionsRef.current;
      const sizeInfo = dimensions ? `${dimensions.width}x${dimensions.height}` : "";
      const filename = `smooth-60fps-recording-${sizeInfo}-${duration}-${timestamp}.mp4`;

      const url = URL.createObjectURL(recordedBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("❌ Failed to download:", error);
      alert("Failed to download recording");
    }
  }, [recordedBlob, formatTime]);

  const handlePreviewRecording = useCallback(() => {
    if (recordingPreview) {
      setShowPreview(true);
    } else if (recordedBlob) {
      const preview = createRecordingPreview(recordedBlob, elapsedSecondsRef.current);
      setRecordingPreview(preview);
      setShowPreview(true);
    } else {
      alert("No recording available to preview");
    }
  }, [recordingPreview, recordedBlob, createRecordingPreview]);

  const handleCloseRecording = useCallback(() => {
    if (isRecording) {
      handleStopRecording();
    }
    setShowPreview(false);
    setRecordingPreview(null);
    setRecordedBlob(null);
    setIsProcessing(false);
    setAnnotationDataUrl("");
    chunksRef.current = [];
    resetTimer();
    mediaRecorderRef.current = null;
    iframeDimensionsRef.current = null;
    window.dispatchEvent(new CustomEvent('feebdak-recording-state', { detail: { isRecording: false } }));
    if (onClose) {
      onClose();
    }
  }, [isRecording, handleStopRecording, resetTimer, onClose]);

  const handleDrawingModeChange = useCallback((enabled: boolean) => {
    setDrawingMode(enabled);
    window.dispatchEvent(new CustomEvent('feebdak-drawing-mode', { detail: { drawingMode: enabled } }));
  }, []);

  const handleToolClick = useCallback(
    (toolId: string) => {
      switch (toolId) {
        case "start":
          handleStartRecording();
          break;
        case "stop":
          handleStopRecording();
          break;
        case "delete":
          handleRestartRecording();
          break;
        case "download":
          handleDownloadRecording();
          break;
        case "preview":
          handlePreviewRecording();
          break;
        case "close":
          handleCloseRecording();
          break;
        case "draw":
          handleDrawingModeChange(!drawingMode);
          break;
      }
      if (onToolClick) {
        onToolClick(toolId);
      }
    },
    [
      handleStartRecording,
      handleStopRecording,
      handleRestartRecording,
      handleDownloadRecording,
      handlePreviewRecording,
      handleCloseRecording,
      handleDrawingModeChange,
      drawingMode,
      onToolClick,
    ]
  );

  const handlePreviewClose = useCallback(() => {
    setShowPreview(false);
    setRecordingPreview(null);
    setRecordedBlob(null);
    setAnnotationDataUrl("");
    resetTimer();
    if (onClose) {
      onClose();
    }
  }, [onClose, resetTimer]);

  const handlePreviewDownload = useCallback(() => {
    handleDownloadRecording();
  }, [handleDownloadRecording]);

  const handlePreviewSubmit = useCallback(
    async (submission: RecordingSubmission) => {
      console.log("📝 Recording submitted with feedback:", submission.feedback);

      const effectiveFeedbackId = submission.uniqueFeedbackId || currentFeedbackIdRef.current;

      let freshBlobUrl: string | null = null;
      try {
        // Create a fresh blob URL from recordingBlob so thumbnail generation doesn't
        // race against ScreenRecordingPreview unmounting and revoking submission.dataUrl.
        if (submission.recordingBlob && submission.recordingBlob.size > 0) {
          freshBlobUrl = URL.createObjectURL(submission.recordingBlob);
        }
        const thumbnailUrl = await generateVideoThumbnail(freshBlobUrl || submission.dataUrl);
        submission.thumbnailUrl = thumbnailUrl;
        console.log("✅ Video thumbnail generated for sidebar");
      } catch (error) {
        console.warn("⚠️ Failed to generate video thumbnail:", error);
      } finally {
        if (freshBlobUrl) {
          URL.revokeObjectURL(freshBlobUrl);
          freshBlobUrl = null;
        }
      }

      if (annotationDataUrl) {
        submission.annotationImage = annotationDataUrl;
        console.log("✅ Including annotation image with submission");
      }

      if (onRecordingSubmit) {
        const finalSubmission: RecordingSubmission = {
          ...submission,
          uniqueFeedbackId: effectiveFeedbackId,
        };
        onRecordingSubmit(finalSubmission);
      }

      setShowPreview(false);
      setRecordingPreview(null);
      setRecordedBlob(null);
      setAnnotationDataUrl("");
      resetTimer();

      if (onClose) {
        onClose();
      }
    },
    [onRecordingSubmit, onClose, resetTimer, annotationDataUrl]
  );

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (recordingPreview?.dataUrl) {
      URL.revokeObjectURL(recordingPreview.dataUrl);
    }
    if (microphoneStreamRef.current) {
      microphoneStreamRef.current.getTracks().forEach(track => track.stop());
      microphoneStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
  }, [recordingPreview]);

  useEffect(() => {
    const consumerId = consumerIdRef.current;
    sharedStreamManager.current.registerConsumer({
      id: consumerId,
      type: "recording",
      onStreamReady: (stream) => {
        console.log("🎥 Recording manager: Shared stream ready");
      },
      onStreamEnded: () => {
        console.log("🎥 Recording manager: Shared stream ended");
        if (isRecording) {
          handleStopRecording();
        }
      },
    });
    return () => {
      sharedStreamManager.current.unregisterConsumer(consumerId);
    };
  }, [isRecording, handleStopRecording]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showPreview) {
          handlePreviewClose();
        } else {
          handleCloseRecording();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showPreview, handlePreviewClose, handleCloseRecording]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const getEffectiveFeedbackId = useCallback(() => {
    return currentFeedbackIdRef.current || uniqueFeedbackId;
  }, [uniqueFeedbackId]);

  const handleAnnotationCanvasReady = useCallback((canvas: HTMLCanvasElement) => {
    annotationCanvasRef.current = canvas;
    console.log("✅ Annotation canvas reference captured");
  }, []);

  return (
    <>
      {isRecording && (
        <PenHighlightOverlay
          isActive={true}
          containerRef={iframeRef}
          isRecording={isRecording}
          onCanvasReady={handleAnnotationCanvasReady}
          drawingMode={drawingMode}
          onDrawingModeChange={handleDrawingModeChange}
          isFullscreen={isFullscreen}
        />
      )}

      {isProcessing && !showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white px-8 py-6 rounded-lg shadow-lg flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
            <span className="text-lg font-medium text-gray-700">Processing smooth 60 FPS video...</span>
          </div>
        </div>
      )}

      {showPreview && recordingPreview && (
        <ScreenRecordingPreview
          preview={recordingPreview}
          onClose={handlePreviewClose}
          onDownload={handlePreviewDownload}
          onSubmit={handlePreviewSubmit}
          uniqueFeedbackId={getEffectiveFeedbackId()}
          annotationImage={annotationDataUrl}
          iframeDimensions={iframeDimensionsRef.current}
        />
      )}

      {!showPreview && (
        <ScreenRecordingToolBox
          isRecording={isRecording}
          recordingTime={formatTime(recordingTime)}
          hasRecording={!!recordedBlob}
          isLoading={isLoading || isProcessing}
          onToolClick={handleToolClick}
          drawingMode={drawingMode}
          maxRecordingSeconds={MAX_RECORDING_SECONDS}
          compact={compact}
          isFullscreen={isFullscreen}
        />
      )}
    </>
  );
};

export default ScreenRecordingManager;